import { useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { SECTION_COLORS } from './TaskCard'

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function getWeekStart(date) {
  const d = new Date(date)
  // Monday = 1, so offset from Sunday (0)
  const day = d.getDay() // 0=Sun,1=Mon,...6=Sat
  const diff = day === 0 ? -6 : 1 - day
  d.setDate(d.getDate() + diff)
  d.setHours(0, 0, 0, 0)
  return d
}

function addDays(date, n) {
  const d = new Date(date)
  d.setDate(d.getDate() + n)
  return d
}

function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

export function TaskCalendar({ tasks, onSelectTask, isDarkMode }) {
  const [weekStart, setWeekStart] = useState(() => getWeekStart(new Date()))

  const days = Array.from({ length: 6 }, (_, i) => addDays(weekStart, i))
  const today = new Date()

  const prevWeek = () => setWeekStart((d) => addDays(d, -7))
  const nextWeek = () => setWeekStart((d) => addDays(d, 7))
  const goToday  = () => setWeekStart(getWeekStart(new Date()))

  // Map tasks to their due date (date string key)
  const tasksByDay = {}
  tasks.forEach((t) => {
    if (!t.due_date) return
    const d = new Date(t.due_date)
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
    if (!tasksByDay[key]) tasksByDay[key] = []
    tasksByDay[key].push(t)
  })

  const getDayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`

  const border = isDarkMode ? 'border-slate-700' : 'border-gray-200'
  const headerBg = isDarkMode ? 'bg-slate-800' : 'bg-white'

  return (
    <div className="flex flex-col h-full">
      {/* Nav bar */}
      <div className={`flex items-center gap-3 px-4 py-3 border-b ${border} ${headerBg} flex-shrink-0`}>
        <button
          onClick={goToday}
          className={`px-3 py-1 text-xs font-medium rounded border transition-colors ${
            isDarkMode
              ? 'border-slate-600 text-slate-300 hover:bg-slate-700'
              : 'border-gray-300 text-gray-600 hover:bg-gray-50'
          }`}
        >
          Today
        </button>
        <button
          onClick={prevWeek}
          className={`p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <button
          onClick={nextWeek}
          className={`p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
        <span className={`text-sm font-semibold ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>
          {weekStart.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}
          {' – '}
          {days[5].toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}
        </span>
      </div>

      {/* Calendar grid */}
      <div className="flex flex-1 overflow-hidden">
        {days.map((day, i) => {
          const isToday = isSameDay(day, today)
          const dayTasks = tasksByDay[getDayKey(day)] ?? []

          return (
            <div
              key={i}
              className={`flex-1 flex flex-col border-r last:border-r-0 min-w-0 ${border} ${
                isDarkMode ? 'bg-slate-900' : 'bg-white'
              }`}
            >
              {/* Day header */}
              <div className={`px-2 py-2 border-b text-center flex-shrink-0 ${border} ${
                isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50'
              }`}>
                <p className={`text-xs font-medium ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                  {DAY_NAMES[i]}
                </p>
                <p className={`text-lg font-bold mt-0.5 w-8 h-8 rounded-full flex items-center justify-center mx-auto ${
                  isToday
                    ? 'bg-sfp-orange text-white'
                    : isDarkMode ? 'text-slate-200' : 'text-gray-800'
                }`}>
                  {day.getDate()}
                </p>
              </div>

              {/* Tasks for this day */}
              <div className="flex-1 overflow-y-auto p-1.5 space-y-1">
                {dayTasks.map((task) => {
                  const sectionColor = SECTION_COLORS[task.section] ?? 'bg-gray-100 text-gray-600'
                  const isOverdue = task.status !== 'completed' && task.status !== 'cancelled'
                    && new Date(task.due_date) < today && !isToday

                  return (
                    <button
                      key={task.id}
                      onClick={() => onSelectTask(task.id)}
                      className={`w-full text-left px-2 py-1.5 rounded text-xs font-medium leading-snug transition-all hover:shadow-sm ${sectionColor} ${
                        isOverdue ? 'ring-1 ring-red-400' : ''
                      } ${task.status === 'completed' ? 'opacity-50 line-through' : ''}`}
                    >
                      <span className="block truncate">
                        {task.claim_number || task.title || 'Untitled'}
                      </span>
                      {task.client_name && task.client_name !== '—' && (
                        <span className="block truncate opacity-70 font-normal mt-0.5">
                          {task.client_name}
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
